import { ComponentCO2, ComponentCO2Link } from '@acies/domain';
import { FormInputEvent } from '@acies/ui';
import NetworkError from '@shared/errors/network-error';
import CO2Model from '../models/co2-model';
import CreateComponentCO2LinkModel from '../models/create-component-co2link-model';
import ListComponentCO2LinkModel from '../models/list-component-co2link-model';
import CO2Service from '../services/co2-service';

export default class CreateComponentCO2LinkController {
	public dialog: HTMLDialogElement | null;

	public set selectedComponentCO2(value: ComponentCO2 | null) {
		if (this.#selectedComponentCO2 === value) {
			return;
		}
		this.#selectedComponentCO2 = value;
		this.#selectedComponentCO2Changed();
	}

	public get selectedComponentCO2(): ComponentCO2 | null {
		return this.#selectedComponentCO2;
	}

	#createComponentCO2LinkModel: CreateComponentCO2LinkModel;
	#listComponentCO2LinkModel: ListComponentCO2LinkModel;
	#co2Service: CO2Service;
	#selectedComponentCO2: ComponentCO2 | null;
	public constructor(co2Model: CO2Model, co2Service: CO2Service) {
		this.#createComponentCO2LinkModel = co2Model.createComponentCO2LinkModel;
		this.#listComponentCO2LinkModel = co2Model.listComponentCO2LinkModel;
		this.#co2Service = co2Service;
	}

	/** When FormInput components in the Edit Dialog dispatches events, they are forwarded to this method. Angular expects the Event type as parameter in event handlers, so we check for the event being an instance of the FormInputEvent to get full type safety and the TSC compiler happy :) */
	public formInputEvent(event: Event): void {
		if (event instanceof FormInputEvent) {
			this.#updateCreateComponentCO2ModelFromEvent(event);
			if (event.type === FormInputEvent.NOT_VALID_VALUE_CHANGED) {
				this.#createComponentCO2LinkModel.createButtonEnabled = false;
			}
		}
	}

	public closeDialog(): void {
		if (this.dialog) {
			this.dialog.close();
			this.#hideAllErrorLabels();
			this.#createComponentCO2LinkModel.resetUIBindings();
		}
	}

	public showDialog(): void {
		if (this.dialog) {
			this.dialog.showModal();
		}
	}

	public createButtonClicked(): void {
		this.#createComponentCO2LinkModel.createSpinnerVisible = true;
		this.#hideAllErrorLabels();
		this.#co2Service.createComponentCO2Link(this.#createComponentCO2LinkModel.createComponentCO2LinkBody).subscribe((response) => {
			this.#createComponentCO2LinkModel.createSpinnerVisible = false;
			// If the response is an instance of ComponentCO2Link,
			// we have created a new ComponentCO2Link successfully.
			if (response instanceof ComponentCO2Link) {
				// We add the newly created ComponentCO2Link instance to the collection here.
				// You could argue that the collection should be completely refreshed,
				// but I choose not to in this case.
				this.#listComponentCO2LinkModel.co2LinkCollection.addItem(response);
				this.#createComponentCO2LinkModel.resetUIBindings();
				this.closeDialog();
			} else if (response === null) {
				// if the response is null, nothing was updated on the server.
				// We can safely close without loading the component-co2's again.
				this.closeDialog();
			} else {
				this.#showErrorLabel(response);
			}
		});
	}

	#hideAllErrorLabels(): void {
		this.#createComponentCO2LinkModel.networkErrorLabelVisible = false;
		this.#createComponentCO2LinkModel.unknownErrorLabelVisible = false;
	}

	#showErrorLabel(error: NetworkError): void {
		if (error instanceof NetworkError) {
			this.#createComponentCO2LinkModel.networkErrorLabelVisible = true;
		} else {
			this.#createComponentCO2LinkModel.unknownErrorLabelVisible = true;
		}
	}

	#selectedComponentCO2Changed(): void {
		if (this.#selectedComponentCO2 instanceof ComponentCO2) {
			this.#createComponentCO2LinkModel.createComponenCO2LinkButtonEnabled = true;
			this.#createComponentCO2LinkModel.currentSelectedComponentCO2Self = this.#selectedComponentCO2.self;
		} else {
			this.#createComponentCO2LinkModel.createComponenCO2LinkButtonEnabled = false;
			this.#createComponentCO2LinkModel.currentSelectedComponentCO2Self = '';
		}
	}

	#updateCreateComponentCO2ModelFromEvent(event: FormInputEvent): void {
		switch (event.key) {
			case 'key':
				this.#createComponentCO2LinkModel.key = event.value;
				break;
			case 'description':
				this.#createComponentCO2LinkModel.description = event.value;
				break;
			default:
				break;
		}
		this.#updateCreateComponentCO2IsValid();
	}

	#updateCreateComponentCO2IsValid(): void {
		this.#createComponentCO2LinkModel.createButtonEnabled = this.#createComponentCO2LinkModel.key !== '';
	}
}
